import { QUERY_KEYS } from "@/constants/queryKeys";
import { useVisible } from "@/hooks/useVisible";
import { isDataConflictError } from "@/utils/error";
import { useQueryClient } from "@tanstack/react-query";

const STANDARD_INDUSTRY_CONFLICT_NAME = "日本標準産業分類";

/**
 * Handles the BE optimistic-concurrency conflict for standard-industry mutations:
 * detects DATA_CONFLICT_ERROR, shows a reload dialog, and on confirm refetches the
 * screen (group + classification queries) then runs `onResolve` (typically closing the edit modal).
 */
export const useStandardIndustryConflictModal = (onResolve: () => void) => {
  const queryClient = useQueryClient();
  const modal = useVisible();

  // Returns true when the error was a conflict (dialog shown) so callers skip normal error handling.
  const handleConflictError = (error: unknown) => {
    if (!isDataConflictError(error)) {
      return false;
    }
    modal.open();
    return true;
  };

  const confirmReload = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_STANDARD_INDUSTRY_GROUP] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_STANDARD_INDUSTRY_CLASSIFICATION] });
    modal.close();
    onResolve();
  };

  return {
    isConflictOpen: modal.visible,
    conflictName: STANDARD_INDUSTRY_CONFLICT_NAME,
    handleConflictError,
    confirmReload,
    cancelConflict: modal.close,
  };
};
